<template>
  <span v-resize="checkMobile" :style="isMobile ? 'padding: 5px !important;' : ''">
    <v-card v-if="client" :loading="isLoading">
      <v-card-text ref="chatContainer" style="scroll-margin-bottom: 150px;">
        <v-row class="space-around d-flex flex-column">
          <!-- Скелетон загрузки для чата -->
          <template v-if="isLoading">
            <v-card flat v-for="i in 3" :key="'skeleton-'+i">
              <v-skeleton-loader
                :loading="true"
                type="list-item-avatar-two-line"
                class="mb-3"
              ></v-skeleton-loader>
            </v-card>
            <v-card flat>
              <v-skeleton-loader
                :loading="true"
                type="list-item-avatar-three-line"
                class="mb-3"
              ></v-skeleton-loader>
            </v-card>
            <v-card flat>
              <v-skeleton-loader
                :loading="true"
                type="list-item-avatar-two-line"
                class="mb-3"
              ></v-skeleton-loader>
            </v-card>
            <v-card flat>
              <v-skeleton-loader
                :loading="true"
                type="list-item-avatar-three-line"
                class="mb-3"
              ></v-skeleton-loader>
            </v-card>
          </template>

          <!-- Сообщения чата -->
          <v-card v-else v-for="message in sortedMessages" :key="message.id" flat>
            <v-list-item :key="message.id" v-if="message.type !== 'outgoing'" class="">
              <v-list-item-avatar class="align-self-start mr-2">
                <v-avatar size="40" color="success" @click="replaceToClientCard()" v-tooltip="{
                  content: `${client.lastname} ${client.name} ${client.middlename} ${client.phone}`,
                  placement: 'bottom-center',
                  classes: ['info'],
                  targetClasses: ['it-has-a-tooltip'],
                  delay: {
                    show: 500,
                    hide: 500,
                  },
                }">
                  {{ client.name ? client.name[0] : '' }} {{ client.lastname ? client.lastname[0] : '' }}
                </v-avatar>
              </v-list-item-avatar>
              <v-list-item-content class="received-message">
                <v-card v-if="message.type === 'text' || (!message.attachments || message.attachments.length === 0)" color="success" class="flex-none">
                  <v-card-text class="white--text pa-2 d-flex flex-column">
                    <span class="text-caption"></span>
                    <span class="align-self-start text-subtitle-1" v-html="parseEmoji(message.text)"></span>
                    <v-icon x-small color="amber" v-if="message.seen === false" class="ml-1">mdi-eye-off-outline</v-icon>
                    <span class="text-caption font-italic align-self-end">
                      <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                      </timeago>
                    </span>
                  </v-card-text>
                </v-card>
                <template v-else v-for="(attachment, idx) in message.attachments">
                  <v-card v-if="attachment.type === 'image'" :key="'img-'+idx" color="success" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <v-img :lazy-src="attachment.thumbnailUrl || attachment.url" max-height="150" max-width="250"
                        :src="attachment.url" @click="downloadFile(attachment.url, attachment.name)"></v-img>
                      <v-icon x-small color="amber" v-if="message.seen === false" class="ml-1">mdi-eye-off-outline</v-icon>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                  <v-card v-else-if="attachment.type === 'audio'" :key="'audio-'+idx" color="success" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <span class="align-self-start text-subtitle-1">
                        <audio controls>
                          <source :src="attachment.url" type="audio/ogg">
                          <source :src="attachment.url" type="audio/mpeg">
                          Ваш браузер не поддерживает аудио элемент.
                        </audio>
                      </span>
                      <v-icon x-small color="amber" v-if="message.seen === false" class="ml-1">mdi-eye-off-outline</v-icon>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                  <v-card v-else-if="attachment.type === 'document'" :key="'doc-'+idx" color="success" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <v-btn color="primary" text @click="downloadFile(attachment.url, attachment.name)">
                        {{ attachment.name || 'Документ' }}
                      </v-btn>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                  <v-card v-else-if="attachment.type === 'video'" :key="'video-'+idx" color="success" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column" style="cursor: pointer;">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <v-img :lazy-src="attachment.thumbnailUrl || attachment.url" max-height="150" max-width="250"
                        :src="attachment.url" @click="downloadFile(attachment.url, attachment.name)">
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-icon color="grey lighten-1" dark size="64"
                              @click="downloadFile(attachment.url, attachment.name)">
                              mdi-youtube
                            </v-icon>
                          </v-row>
                        </template>
                      </v-img>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                </template>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-else :key="'sent-message-'+message.id">
              <v-list-item-content class="sent-message justify-end">
                <v-card v-if="message.type === 'text' || (!message.attachments || message.attachments.length === 0)" color="primary" class="flex-none">
                  <v-card-text class="white--text pa-2 d-flex flex-column">
                    <span class="text-caption"></span>
                    <span class="align-self-start text-subtitle-1" v-html="parseEmoji(message.text)"></span>
                    <span class="text-caption font-italic align-self-end">
                      <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                      </timeago>
                    </span>
                  </v-card-text>
                </v-card>
                <template v-else v-for="(attachment, idx) in message.attachments">
                  <v-card v-if="attachment.type === 'image'" :key="'img-'+idx" color="primary" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <v-img :lazy-src="attachment.thumbnailUrl || attachment.url" max-height="150" max-width="250"
                        :src="attachment.url" @click="downloadFile(attachment.url, attachment.name)"></v-img>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                  <v-card v-else-if="attachment.type === 'audio'" :key="'audio-'+idx" color="primary" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <span class="align-self-start text-subtitle-1">
                        <audio controls>
                          <source :src="attachment.url" type="audio/ogg">
                          <source :src="attachment.url" type="audio/mpeg">
                          Ваш браузер не поддерживает аудио элемент.
                        </audio>
                      </span>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                  <v-card v-else-if="attachment.type === 'document'" :key="'doc-'+idx" color="primary" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <v-btn color="secondary" text @click="downloadFile(attachment.url, attachment.name)">
                        {{ attachment.name || 'Документ' }}
                      </v-btn>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                  <v-card v-else-if="attachment.type === 'video'" :key="'video-'+idx" color="primary" class="flex-none">
                    <v-card-text class="white--text pa-2 d-flex flex-column" style="cursor: pointer;">
                      <span class="text-caption">{{ message.text || '' }}</span>
                      <v-img :lazy-src="attachment.thumbnailUrl || attachment.url" max-height="150" max-width="250"
                        :src="attachment.url" @click="downloadFile(attachment.url, attachment.name)">
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-icon color="grey lighten-1" dark size="64"
                              @click="downloadFile(attachment.url, attachment.name)">
                              mdi-youtube
                            </v-icon>
                          </v-row>
                        </template>
                      </v-img>
                      <span class="text-caption font-italic align-self-end">
                        <timeago :datetime="formatMessageTime(message.createdAt || message.timestamp)" :auto-update="60" :locale="$i18n.locale">
                        </timeago>
                      </span>
                    </v-card-text>
                  </v-card>
                </template>
              </v-list-item-content>
              <v-list-item-avatar class="align-self-start ml-2" @click="replaceToClientCard()">
                <v-avatar size="40" color="primary">
                  {{ $t('custom.you') }}
                </v-avatar>
              </v-list-item-avatar>
            </v-list-item>
          </v-card>
        </v-row>
        <v-footer fixed>
          <v-container class="ma-0 pa-0">
            <v-row no-gutters>
              <v-col>
                <div v-if="isLoading" class="d-flex flex-row align-center">
                  <v-skeleton-loader
                    :loading="true"
                    type="button"
                    class="mr-2"
                    width="60"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    :loading="true"
                    type="text"
                    width="100%"
                    height="56"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    :loading="true"
                    type="button"
                    class="ml-4"
                    width="40"
                  ></v-skeleton-loader>
                </div>
                <div v-else class="d-flex flex-row align-center">
                  <v-file-input class="flex-none" hide-input hide-details flat truncate-length="15"
                    v-model="attachment" dense inline solo></v-file-input>
                  <v-textarea rows="1" autofocus auto-grow v-model="newMessage"
                    :placeholder="$t('companies.type_something')" @keypress.enter="sendMessage()"></v-textarea>
                  <v-btn icon class="ml-4" @click="sendMessage()">
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-footer>
      </v-card-text>
    </v-card>
  </span>
</template>
<script>
import moment from "moment";
import { setMomentLocale } from "@/utils/localeInfra";
import "moment/locale/ru";
import "moment/locale/en-gb";
import { saveAs } from "file-saver";
import PullToRefresh from "pulltorefreshjs";
import CableSubscriber from "@/mixins/CableSubscriber";

export default {
  mixins: [CableSubscriber],
  data() {
    return {
      client: null,
      dialogId: null,
      newMessage: "",
      attachment: null,
      isMobile: false,
    };
  },
  computed: {
    company() {
      if (this.$store.getters.getCompany) {
        return this.$store.getters.getCompany.company;
      } else {
        return null;
      }
    },
    isLoading() {
      return this.$store.getters.getTeletypeLoading;
    },
    messages() {
      if (!this.dialogId) return [];
      let messages = this.$store.getters.getTeletypeDialogMessages(this.dialogId) || [];
      messages = messages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return messages;
    },
    activeDialog() {
      return this.$store.getters.getTeletypeActiveDialog;
    },
    sortedMessages() {
      return [...this.messages].sort((a, b) => {
        const timeA = this.getMessageTimestamp(a);
        const timeB = this.getMessageTimestamp(b);
        return timeA - timeB;  // От старых к новым
      });
    }
  },
  mounted() {
    let self = this;
    const ptr = PullToRefresh.init({
      mainElement: "body",
      distIgnore: 100,
      instructionsPullToRefresh: this.$t("custom.pull_to_refresh"),
      instructionsReleaseToRefresh: this.$t("custom.release_to_refresh"),
      instructionsRefreshing: this.$t("custom.refreshing"),
      onRefresh() {
        return self.loadMessages();
      }
    });

    // Сбрасываем счетчик новых сообщений при открытии чата
    this.$store.commit("resetTeletypeMessagesCount");
  },
  created() {
    // Установка локали для moment
    setMomentLocale(this.$i18n.locale);

    if (this.$store.getters.isLoggedIn) {
      // Загружаем информацию о клиенте
      this.$store.commit('setTeletypeLoading', true);

      this.axios
        .get(`/api/v1/clients/${this.$route.params.id}`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.client = response.data.client;
          // Находим или создаем диалог для клиента
          this.findOrCreateDialog();
        })
        .catch((error) => {
          // 404 = клиента нет (удалён / чужой филиал / битый id): уводим на
          // список вместо пустой карточки. Прочее — через setError.
          if (error.response?.status === 404) {
            this.$notify(this.$t("errors.client_not_found"), "", "error");
            this.$router.push({ path: `/clients` });
            return;
          }
          this.setError(error, this.$t("errors.client_load"));
        })
        .finally(() => {
          this.$store.commit('setTeletypeLoading', false);
        });
    } else {
      this.$store.commit("unsetCurrentUser");
    }
  },
  beforeDestroy() {
    // FT-066: TeletypeDialog sub auto-unsubscribed by CableSubscriber mixin.
  },
  methods: {
    onTeletypeDialogConnected() {
      console.log("Connected to TeletypeDialog for dialog:", this.dialogId);
    },
    // Moved 1:1 from the old TeletypeDialog received handler (arrow → `this` is the component).
    onTeletypeDialogReceived(data) {
      console.log("Received dialog data TeletypeClient:", data);
      if (!data || !data.name || !data.payload) return;

      try {
        // Парсим JSON из payload
        const payloadData = JSON.parse(data.payload);

        // Обработка события успешной отправки сообщения
        if (data.name === "success send" && payloadData.message) {
          const message = payloadData.message;
          // Добавляем сообщение в стор
          this.$store.commit('addTeletypeDialogMessage', {
            dialogId: this.dialogId,
            message: message
          });
          this.$nextTick(() => this.scrollToBottom());
        } else if (data.name === "message status change" && payloadData.messageId) {
          // Обработка события изменения статуса сообщения
          // Обновляем статус сообщения в сторе
          this.$store.commit('updateTeletypeMessageStatus', {
            dialogId: this.dialogId,
            messageId: payloadData.messageId,
            status: payloadData.messageStatus
          });
        } else if (data.name === "new message" && payloadData.message) {
          const message = payloadData.message;
          // Устанавливаем seen: false для новых сообщений от клиента
          if (message.isItClient || message.isOutgoing === false) {
            message.seen = false;
          }
          console.log("New message in TeletypeClient:", message);
          // Добавляем сообщение в стор
          this.$store.commit('addTeletypeDialogMessage', {
            dialogId: this.dialogId,
            message: message
          });
          this.$nextTick(() => this.scrollToBottom());
        }
      } catch (error) {
        console.error("Ошибка при обработке WebSocket сообщения:", error);
      }
    },
    findOrCreateDialog() {
      if (!this.client || !this.client.phone) {
        this.setError(null, this.$t("teletype.client_no_phone"));
        return;
      }

      // Загружаем диалоги через действие Vuex
      this.$store.dispatch('loadTeletypeDialogs')
        .then(dialogs => {
          // Ищем диалог с клиентом
          const clientPhone = this.client.phone.replace(/[^\d]/g, "");
          const dialog = dialogs.find(d => {
            return d.client && d.client.phone && d.client.phone.replace(/[^\d]/g, "") === clientPhone;
          });

          if (dialog) {
            // Диалог найден, загружаем сообщения
            this.dialogId = dialog.id;
            this.$store.commit('setTeletypeActiveDialog', dialog);
            this.loadMessages();

            // Подписываемся на канал диалога
            this.subscribeToDialogChannel();
          } else {
            // Создаем новый диалог
            this.createNewDialog();
          }
        })
        .catch(error => {
          this.setError(error, this.$t("teletype.dialogs_load"));
        });
    },

    createNewDialog() {
      // Создаем временный диалог (в реальном приложении запрос к API)
      this.dialogId = `client_${this.client.id}_${Date.now()}`;

      const newDialog = {
        id: this.dialogId,
        client: this.client,
        title: `${this.client.name} ${this.client.lastname}`,
        unreadCount: 0,
        hasUnreadMessages: false
      };

      this.$store.commit('addTeletypeDialog', newDialog);
      this.$store.commit('setTeletypeActiveDialog', newDialog);
      this.$store.commit('setTeletypeDialogMessages', { dialogId: this.dialogId, messages: [] });

      // Подписываемся на канал диалога
      this.subscribeToDialogChannel();
    },

    subscribeToDialogChannel() {
      if (!this.dialogId) return;

      // FT-066: same-room no-op — уже подписаны на этот dialog, не пересоздаём sub
      if (this.teletypeDialogSub && this.teletypeDialogRoom === this.dialogId) return;

      // Switch: отписываемся от предыдущего диалога (held ref), подписываемся на текущий
      if (this.teletypeDialogSub) {
        this.$cableUnsubscribe(this.teletypeDialogSub);
        this.teletypeDialogSub = null;
        this.teletypeDialogRoom = null;
      }

      this.teletypeDialogSub = this.$cableSubscribe(
        { channel: "TeletypeDialog", room: this.dialogId },
        {
          connected: () => this.onTeletypeDialogConnected(),
          received: (data) => this.onTeletypeDialogReceived(data),
        },
      );
      this.teletypeDialogRoom = this.dialogId;
    },

    loadMessages() {
      if (!this.dialogId) return;

      // Загружаем сообщения через действие Vuex
      this.$store.dispatch('loadTeletypeDialogMessages', this.dialogId)
        .then(() => {
          this.$nextTick(() => this.scrollToBottom());

          // Отмечаем все сообщения как прочитанные
          if (this.messages && this.messages.length > 0) {
            this.messages.forEach(message => {
              if (message.seen === false) {
                this.markMessageAsRead(message);
              }
            });
          }
        })
        .catch(error => {
          this.setError(error, this.$t("teletype.messages_load"));
        });
    },

    sendMessage() {
      if ((!this.newMessage || this.newMessage.trim() === '') && !this.attachment) {
        this.$notify(this.$t("errors.error"), this.$t("teletype.message_empty"), "error", 5000);
        return;
      }

      if (!this.dialogId) {
        this.$notify(this.$t("errors.error"), this.$t("teletype.no_dialog"), "error", 5000);
        return;
      }

      // Отправляем сообщение через действие Vuex
      this.$store.dispatch('sendTeletypeMessage', {
        dialogId: this.dialogId,
        text: this.newMessage,
        file: this.attachment
      })
      .then(() => {
        this.newMessage = "";
        this.attachment = null;
        this.$nextTick(() => this.scrollToBottom());
      })
      .catch(error => {
        this.setError(error, this.$t("teletype.message_send"));
      });
    },

    formatTime(timestamp) {
      return moment.unix(timestamp).toDate();
    },

    formatMessageTime(timeObj) {
      if (!timeObj) return new Date();
      if (typeof timeObj === 'number') return moment.unix(timeObj).toDate();
      return timeObj.date ? new Date(timeObj.date) : new Date();
    },

    getMessageTimestamp(message) {
      if (message.createdAt?.date) {
        return new Date(message.createdAt.date).getTime();
      }
      if (message.timestamp) {
        return typeof message.timestamp === 'number'
          ? message.timestamp * 1000
          : new Date(message.timestamp).getTime();
      }
      return 0;
    },

    scrollToBottom() {
      const container = this.$refs.chatContainer;
      if (container) {
        const scrollHeight = container.scrollHeight;
        container.scrollTo({
          top: scrollHeight,
          behavior: 'smooth'
        });
      }
    },

    downloadFile(url, fileName) {
      if (url) {
        saveAs(url, fileName || 'file');
      }
    },

    replaceToClientCard() {
      this.$router.push({
        path: `/clients/${this.client.id}`,
      });
    },

    checkMobile() {
      if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
        this.isMobile = true;
        return true;
      } else {
        this.isMobile = false;
        return false;
      }
    },

    parseEmoji(text) {
      if (!text) return '';

      // Преобразуем :emoji: коды в изображения смайлов
      const emojiMap = {
        ':blush:': '😊',
        ':smile:': '😃',
        ':laughing:': '😄',
        ':grin:': '😁',
        ':joy:': '😂',
        ':smiley:': '😀',
        ':rofl:': '🤣',
        ':relaxed:': '☺️',
        ':wink:': '😉',
        ':heart:': '❤️',
        ':heart_eyes:': '😍',
        ':kissing_heart:': '😘',
        ':kissing:': '😗',
        ':kissing_smiling_eyes:': '😙',
        ':kissing_closed_eyes:': '😚',
        ':thumbsup:': '👍',
        ':thumbsdown:': '👎',
        ':ok_hand:': '👌',
        ':punch:': '👊',
        ':facepunch:': '👊',
        ':fist:': '✊',
        ':v:': '✌️',
        ':wave:': '👋',
        ':hand:': '✋',
        ':raised_hand:': '✋',
        ':raised_hands:': '🙌',
        ':pray:': '🙏',
        ':rocket:': '🚀',
        ':tada:': '🎉',
        ':fire:': '🔥',
        ':100:': '💯',
        ':clap:': '👏',
        ':+1:': '👍',
        ':-1:': '👎',
        ':bust_silhouette:': '👤',
        ':mobile_calling:': '📱',
        ':mobile_phone:': '📱',
        ':phone:': '☎️',
        ':oncoming_automobile:': '🚘',
        ':car:': '🚗',
        ':automobile:': '🚗',
        ':round_pushpin:': '📍',
        ':deciduous_tree:': '🌳',
        ':herb:': '🌿',
        ':credit_card:': '💳',
        ':timer:': '⏰',
        ':timer_clock:': '⏰',
        ':clock:': '🕒',
        ':four_leaf_clover:': '🍀'
      };

      let formattedText = text;

      // Заменяем эмодзи-коды на Unicode символы
      Object.keys(emojiMap).forEach(code => {
        const regex = new RegExp(code, 'g');
        formattedText = formattedText.replace(regex, emojiMap[code]);
      });

      // Экранируем HTML
      formattedText = formattedText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

      // Заменяем переводы строки на <br>
      formattedText = formattedText.replace(/\n/g, '<br>');

      return formattedText;
    },

    markMessageAsRead(message) {
      if (message.seen === false) {
        // Обновляем статус сообщения
        this.$store.commit('updateTeletypeMessage', {
          dialogId: this.dialogId,
          messageId: message.id,
          updates: { seen: true }
        });

        // Отправляем на сервер информацию о прочтении сообщения
        // (если требуется API-вызов)
      }
    },
  },
};
</script>
<style scoped>
.chat-message {
  display: unset !important;
  white-space: break-spaces;
}

.chat-screen {
  max-height: 320px;
  overflow-y: auto;
}

.flex-none {
  flex: unset;
}

.received-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: 54px;
  right: auto;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #4caf50 transparent transparent transparent;
}

.sent-message::after {
  content: ' ';
  position: absolute;
  width: 0;
  height: 0;
  left: auto;
  right: 54px;
  top: 12px;
  bottom: auto;
  border: 12px solid;
  border-color: #1976d2 transparent transparent transparent;
}
</style>
